// Leitura/validação client-side da planilha de importação de funcionários
// (roadmap #37). Espelha exatamente o layout gerado por
// modeloImportacaoFuncionarios.js — mesma ordem de colunas, mesma aba
// "Funcionarios", mesma referência de "Empresa para Selecao".
import { read, utils } from "xlsx";
import { COLUNAS_FUNCIONARIOS, montarEmpresasParaModelo } from "./modeloImportacaoFuncionarios";

const NOME_ABA_FUNCIONARIOS = "Funcionarios";
const REGEX_DATA_ISO = /^\d{4}-\d{2}-\d{2}$/;

function normalizarSituacaoImportacao(valor) {
  const texto = String(valor ?? "").trim().toLowerCase();
  if (!texto) return "A";
  if (texto === "ativo") return "A";
  if (texto === "inativo") return "I";
  return null; // valor não reconhecido
}

function validarData(valor, rotulo, motivos) {
  const texto = String(valor ?? "").trim();
  if (!texto) return undefined;
  if (!REGEX_DATA_ISO.test(texto)) {
    motivos.push(`${rotulo} inválida (use AAAA-MM-DD): "${texto}".`);
    return undefined;
  }
  return texto;
}

// Lê o ArrayBuffer do arquivo .xlsx enviado pelo usuário e devolve:
//   { validos, invalidos, totalLinhas } — validos/invalidos já no formato
//   aceito pelo endpoint de importação (id_empresa resolvido, situacao A/I).
export function parsePlanilhaFuncionarios(arrayBuffer, empresas) {
  const empresasModelo = montarEmpresasParaModelo(empresas);
  const mapaEmpresas = new Map(
    empresasModelo.map((modelo, indice) => [modelo["Empresa para Selecao"], empresas[indice]])
  );

  const workbook = read(arrayBuffer, { type: "array" });
  const sheet = workbook.Sheets[NOME_ABA_FUNCIONARIOS];
  if (!sheet) {
    throw new Error(`Aba "${NOME_ABA_FUNCIONARIOS}" não encontrada na planilha.`);
  }

  const linhas = utils.sheet_to_json(sheet, { header: 1, raw: false, defval: null });
  const [cabecalho, ...dados] = linhas;
  const cabecalhoValido =
    Array.isArray(cabecalho) &&
    COLUNAS_FUNCIONARIOS.every((coluna, i) => (cabecalho[i] || "").toString().trim() === coluna);
  if (!cabecalhoValido) {
    throw new Error(
      `Cabeçalho da aba "${NOME_ABA_FUNCIONARIOS}" não corresponde ao modelo esperado. Baixe o modelo atualizado e refaça o preenchimento.`
    );
  }

  const validos = [];
  const invalidos = [];

  dados.forEach((linha, indice) => {
    const numeroLinha = indice + 2; // linha 1 = cabeçalho
    const [nome, cpf, nascimento, dataContratacao, dataDemissao, empresaTexto, situacaoTexto] = linha || [];

    const linhaVazia = ![nome, cpf, nascimento, dataContratacao, dataDemissao, empresaTexto]
      .some((valor) => String(valor ?? "").trim() !== "");
    if (linhaVazia) return;

    const motivos = [];
    const nomeTrim = String(nome ?? "").trim();
    if (!nomeTrim) motivos.push("Nome é obrigatório.");

    const empresaTrim = String(empresaTexto ?? "").trim();
    const empresa = mapaEmpresas.get(empresaTrim);
    if (!empresaTrim) {
      motivos.push("Empresa é obrigatória.");
    } else if (!empresa) {
      motivos.push(`Empresa "${empresaTrim}" não encontrada na aba "Empresas Vinculadas".`);
    }

    const nascimentoIso = validarData(nascimento, "Data de Nascimento", motivos);
    const dataContratacaoIso = validarData(dataContratacao, "Data de Contratacao", motivos);
    const dataDemissaoIso = validarData(dataDemissao, "Data de Demissao", motivos);

    const situacao = normalizarSituacaoImportacao(situacaoTexto);
    if (situacao === null) {
      motivos.push(`Situação inválida: "${situacaoTexto}" (use Ativo ou Inativo).`);
    }

    const linhaResultado = {
      linha: numeroLinha,
      nome: nomeTrim,
      cpf: String(cpf ?? "").trim() || undefined,
      nascimento: nascimentoIso,
      data_contratacao: dataContratacaoIso,
      data_demissao: dataDemissaoIso,
      empresaTexto: empresaTrim,
      id_empresa: empresa?.id,
      situacao: situacao || "A",
    };

    if (motivos.length > 0) {
      invalidos.push({ ...linhaResultado, motivos });
    } else {
      validos.push(linhaResultado);
    }
  });

  return { validos, invalidos, totalLinhas: validos.length + invalidos.length };
}
